import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Copy,
  Search,
  Filter,
  Eye
} from 'lucide-react';

interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  message: string;
  category: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

const NotificationTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState<NotificationTemplate | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    message: '',
    category: 'general',
    isActive: true
  });

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'exam', label: 'Examinations' },
    { value: 'fee', label: 'Fee Related' },
    { value: 'event', label: 'Events' },
    { value: 'holiday', label: 'Holidays' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'emergency', label: 'Emergency' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // Mock data - would be loaded from API
      const mockTemplates: NotificationTemplate[] = [
        {
          id: '1',
          name: 'Exam Schedule Notification',
          subject: 'Important: Upcoming Examination Schedule',
          message: 'Dear Parents and Students,\n\nWe would like to inform you about the upcoming examination schedule:\n\n[EXAM_DETAILS]\n\nPlease ensure students are well-prepared and arrive on time.\n\nBest regards,\nSchool Administration',
          category: 'exam',
          isActive: true,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z',
          usageCount: 15
        },
        {
          id: '2',
          name: 'Fee Payment Reminder',
          subject: 'Fee Payment Reminder - [MONTH]',
          message: 'Dear Parents,\n\nThis is a gentle reminder that the school fee payment for [MONTH] is due on [DUE_DATE].\n\nAmount: [AMOUNT]\nDue Date: [DUE_DATE]\n\nPlease make the payment at your earliest convenience to avoid any inconvenience.\n\nThank you,\nAccounts Department',
          category: 'fee',
          isActive: true,
          createdAt: '2024-01-10T09:00:00Z',
          updatedAt: '2024-01-10T09:00:00Z',
          usageCount: 25
        },
        {
          id: '3',
          name: 'Holiday Announcement',
          subject: 'School Holiday Notification',
          message: 'Dear Parents and Students,\n\nWe would like to inform you that the school will remain closed on [DATE] due to [REASON].\n\nClasses will resume on [RESUME_DATE].\n\nWe wish you a pleasant holiday!\n\nWarm regards,\nSchool Management',
          category: 'holiday',
          isActive: true,
          createdAt: '2024-01-05T08:00:00Z',
          updatedAt: '2024-01-05T08:00:00Z',
          usageCount: 8
        },
        {
          id: '4',
          name: 'Parent-Teacher Meeting',
          subject: 'Invitation: Parent-Teacher Meeting',
          message: 'Dear Parents,\n\nYou are cordially invited to attend the Parent-Teacher Meeting scheduled for:\n\nDate: [DATE]\nTime: [TIME]\nVenue: [VENUE]\n\nThis is an excellent opportunity to discuss your child\'s academic progress and development.\n\nLooking forward to your presence.\n\nBest regards,\nSchool Administration',
          category: 'event',
          isActive: true,
          createdAt: '2024-01-01T07:00:00Z',
          updatedAt: '2024-01-01T07:00:00Z',
          usageCount: 12
        }
      ];
      setTemplates(mockTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const newTemplate: NotificationTemplate = {
        id: Date.now().toString(),
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usageCount: 0
      };

      setTemplates([newTemplate, ...templates]);
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    try {
      const updatedTemplate = {
        ...editingTemplate,
        ...formData,
        updatedAt: new Date().toISOString()
      };

      setTemplates(templates.map(t => t.id === editingTemplate.id ? updatedTemplate : t));
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      console.error('Error updating template:', error);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      setTemplates(templates.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleDuplicateTemplate = (template: NotificationTemplate) => {
    const duplicatedTemplate: NotificationTemplate = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0
    };

    setTemplates([duplicatedTemplate, ...templates]);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      message: '',
      category: 'general',
      isActive: true
    });
  };

  const startEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      message: template.message,
      category: template.category,
      isActive: template.isActive
    });
    setShowCreateForm(true);
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingTemplate(null);
            resetForm();
          }}
          className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Template</span>
        </button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-[#211c15] border border-[#3a3122] rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-[#f3ede2] mb-1">{template.name}</h3>
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    categories.find(c => c.value === template.category)?.value === 'emergency'
                      ? 'bg-[#341a17] text-[#f7aca6]'
                      : categories.find(c => c.value === template.category)?.value === 'exam'
                      ? 'bg-[#332711] text-[#f5c876]'
                      : 'bg-[#332217] text-[#f0a67d]'
                  }`}>
                    {categories.find(c => c.value === template.category)?.label}
                  </span>
                  {!template.isActive && (
                    <span className="px-2 py-1 bg-[#b9ad9b] text-[#6b6154] rounded-full text-xs font-medium">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#8a7f6e] mb-2">Used {template.usageCount} times</p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-[#b9ad9b] mb-1">Subject:</p>
              <p className="text-sm text-[#b9ad9b] line-clamp-2">{template.subject}</p>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-[#b9ad9b] mb-1">Message Preview:</p>
              <p className="text-sm text-[#b9ad9b] line-clamp-3">{template.message}</p>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPreviewTemplate(template)}
                className="flex-1 px-3 py-1.5 text-sm bg-[#211c15] hover:bg-[#28221a] text-[#b9ad9b] rounded transition-colors flex items-center justify-center space-x-1"
              >
                <Eye className="w-3 h-3" />
                <span>Preview</span>
              </button>
              <button
                onClick={() => handleDuplicateTemplate(template)}
                className="px-3 py-1.5 text-sm bg-[#211c15] hover:bg-[#28221a] text-[#b9ad9b] rounded transition-colors"
              >
                <Copy className="w-3 h-3" />
              </button>
              <button
                onClick={() => startEdit(template)}
                className="px-3 py-1.5 text-sm bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded transition-colors"
              >
                <Edit3 className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleDeleteTemplate(template.id)}
                className="px-3 py-1.5 text-sm bg-[#f38b83] hover:bg-[#f38b83] text-white rounded transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#f3ede2] mb-2">No templates found</h3>
          <p className="text-[#8a7f6e] mb-4">
            {searchTerm || filterCategory !== 'all'
              ? 'No templates match your search criteria.'
              : 'Create your first notification template to get started.'
            }
          </p>
          <button
            onClick={() => {
              setShowCreateForm(true);
              setEditingTemplate(null);
              resetForm();
            }}
            className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
          >
            Create Template
          </button>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#211c15] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#3a3122]">
              <h2 className="text-xl font-semibold text-[#f3ede2]">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingTemplate(null);
                  resetForm();
                }}
                className="text-[#8a7f6e] hover:text-[#b9ad9b]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                    placeholder="Enter template name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  placeholder="Enter notification subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  rows={8}
                  className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                  placeholder="Enter template message. Use placeholders like [NAME], [DATE], [AMOUNT] for dynamic content."
                  required
                />
                <p className="text-xs text-[#8a7f6e] mt-1">
                  Tip: Use placeholders like [NAME], [DATE], [AMOUNT] for dynamic content
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                />
                <label htmlFor="isActive" className="text-sm text-[#b9ad9b]">
                  Template is active
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-[#3a3122]">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingTemplate(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                disabled={!formData.name || !formData.subject || !formData.message}
                className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{editingTemplate ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#211c15] rounded-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-[#3a3122]">
              <h2 className="text-xl font-semibold text-[#f3ede2]">Template Preview</h2>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="text-[#8a7f6e] hover:text-[#b9ad9b]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="bg-[#28221a] border border-[#3a3122] rounded-lg p-4">
                <div className="text-sm font-medium text-[#f3ede2] mb-2">{previewTemplate.subject}</div>
                <div className="text-sm text-[#b9ad9b] whitespace-pre-wrap">{previewTemplate.message}</div>
              </div>
            </div>

            <div className="flex items-center justify-end p-6 border-t border-[#3a3122]">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationTemplates;